/* =============================================================================
 *  Kreux Matrix — Page 4 · Performance Layer
 *  "$10M Enterprise — Zero-Hallucination Protocol · Continuous Active State"
 *
 *  Self-contained · Brand vars only · Zero state · Zero hover triggers.
 * ===========================================================================*/

const dict = {
  en: {
    Title: "The Asymmetric Advantage.",
    Subtitle: "Adapt or Obsolete.",
    Body: "The market does not wait for manual labor. The Kreux Matrix translates operational friction into absolute profit. Review the industry architecture.",
    MicDrop: "The numbers are absolute. The choice of trajectory is yours.",
    MicDropAccent: "absolute",
    Card1_Sector: "Healthcare, Hospitality & Service",
    Card1_Title: "High-Volume Intake",
    Card1_Pain: "INDUSTRY FRICTION: 30% peak-hour call abandonment. 85% of missed callers never return.",
    Card1_Outcome: "KREUX OUTCOME: Infinite Concurrency. Zero missed revenue.",
    Card1_Status: "VOICE_NODE: INTERCEPTED",
    Card2_Sector: "E-Commerce, Real Estate & SaaS",
    Card2_Title: "The Speed-to-Lead Pipeline",
    Card2_Pain: "INDUSTRY FRICTION: 391% drop in lead conversion if response time exceeds 5 minutes.",
    Card2_Outcome: "KREUX OUTCOME: 400ms Interception Velocity. Flawless routing.",
    Card2_Status: "INBOX_MATRIX: SYNTHESIZED",
    Card3_Sector: "Finance, Legal & Enterprise",
    Card3_Title: "Proprietary Data Vaults",
    Card3_Pain: "INDUSTRY FRICTION: 1.8 hours wasted daily per employee on data retrieval. Extreme risk of public AI leaks.",
    Card3_Outcome: "KREUX OUTCOME: 100% Data Sovereignty. Military-grade isolation.",
    Card3_Status: "VAULT: ISOLATED"
  },
  de: {
    Title: "Der asymmetrische Vorteil.",
    Subtitle: "Adaptieren oder Veralten.",
    Body: "Der Markt wartet nicht auf manuelle Arbeit. Die Kreux-Matrix verwandelt operative Reibung in absoluten Profit. Prüfen Sie die Branchenarchitektur.",
    MicDrop: "Die Zahlen sind absolut. Die Wahl der Flugbahn liegt bei Ihnen.",
    MicDropAccent: "absolut",
    Card1_Sector: "Gesundheitswesen, Gastronomie & Service",
    Card1_Title: "Hochvolumen-Aufnahme",
    Card1_Pain: "BRANCHENREIBUNG: 30% Abbruchquote zu Spitzenzeiten. 85% der verpassten Anrufer kehren nie zurück.",
    Card1_Outcome: "KREUX ERGEBNIS: Unendliche Parallelität. Null entgangener Umsatz.",
    Card1_Status: "VOICE_NODE: INTERCEPTED",
    Card2_Sector: "E-Commerce, Immobilien & SaaS",
    Card2_Title: "Die Speed-to-Lead-Pipeline",
    Card2_Pain: "BRANCHENREIBUNG: 391% Einbruch der Lead-Konvertierung, wenn die Reaktionszeit 5 Minuten überschreitet.",
    Card2_Outcome: "KREUX ERGEBNIS: 400ms Reaktionsgeschwindigkeit. Fehlerfreies Routing.",
    Card2_Status: "INBOX_MATRIX: SYNTHESIZED",
    Card3_Sector: "Finanzen, Recht & Enterprise",
    Card3_Title: "Proprietäre Datentresore",
    Card3_Pain: "BRANCHENREIBUNG: 1,8 Stunden Zeitverlust pro Mitarbeiter durch Datenrecherche. Extremes Risiko bei öffentlichen KI-Modellen.",
    Card3_Outcome: "KREUX ERGEBNIS: 100% Datensouveränität. Isolierung auf Militärniveau.",
    Card3_Status: "VAULT: ISOLATED"
  }
};

/* ─── Background Hexagon Matrix ───────────────────────────────────────── */
const HexMatrix = () => (
  <svg
    aria-hidden="true"
    width="100%"
    height="100%"
    xmlns="http://www.w3.org/2000/svg"
    className="absolute inset-0"
  >
    <defs>
      <pattern id="kx-hex-zh" x="0" y="0" width="56" height="98" patternUnits="userSpaceOnUse">
        <path
          d="M28 0 L56 16 L56 48 L28 64 L0 48 L0 16 Z M0 48 L0 80 L28 96 L56 80 L56 48"
          fill="none"
          stroke="rgba(255,255,255,0.03)"
          strokeWidth="1"
        />
      </pattern>
    </defs>
    <rect width="100%" height="100%" fill="url(#kx-hex-zh)" />
  </svg>
);

/* ─── Card 1 · Voice Interceptor (8 staggered bars) ───────────────────── */
const Card1Story = () => {
  const delays = [0.0, 0.12, 0.24, 0.36, 0.48, 0.60, 0.72, 0.84];
  return (
    <div className="h-32 flex flex-col justify-end gap-2">
      <div className="flex items-end gap-2 h-full">
        {delays.map((d, i) => (
          <span
            key={i}
            className="pl-vBar w-2 rounded-full"
            style={{ animationDelay: `${d}s` }}
          />
        ))}
      </div>
    </div>
  );
};

/* ─── Card 2 · Email Synthesizer (3 sorting rows) ─────────────────────── */
const Card2Story = () => {
  const rowDelays = [0, 100, 200];
  return (
    <div className="h-32 flex flex-col justify-center gap-3">
      {rowDelays.map((d, i) => (
        <div key={i} className="flex gap-2 items-center">
          <span
            className="pl-syncL h-1.5 rounded-full"
            style={{ animationDelay: `${d}ms` }}
          />
          <span
            className="pl-syncR h-1.5 rounded-full"
            style={{ animationDelay: `${d}ms` }}
          />
        </div>
      ))}
    </div>
  );
};

/* ─── Card 3 · Secure Vault (spin ring + rotated core + pulsing accent) ─ */
const Card3Story = () => (
  <div className="h-32 flex items-center gap-4">
    <div
      className="relative w-20 h-20 rounded-full border-2 border-[var(--il-cyan)] flex items-center justify-center"
      style={{
        boxShadow:
          "0 0 22px var(--il-cyan), inset 0 0 14px rgba(0,229,255,0.30)",
      }}
    >
      {/* Spinning inner dashed ring */}
      <span className="pl-vSpin absolute inset-1 rounded-full border border-dashed border-white/30" />
      {/* Center square rotated 45° */}
      <span
        className="w-5 h-5 bg-[var(--il-cyan)] rotate-45"
        style={{ boxShadow: "0 0 18px var(--il-cyan)" }}
      />
    </div>
    {/* Horizontal pulsing accent line */}
    <span
      className="pl-vAccent block h-1.5 rounded-full"
      style={{ boxShadow: "0 0 14px var(--il-cyan)" }}
    />
  </div>
);

const PerformanceLayer = ({ language = "en" }) => {
  const t = dict[language] || dict["en"];

  /* Pre-build mic-drop with the accent word wrapped in cyan */
  const renderMicDrop = () => {
    const parts = t.MicDrop.split(t.MicDropAccent);
    if (parts.length < 2) {
      return <>{t.MicDrop}</>;
    }
    return (
      <>
        {parts[0]}
        <span className="text-[var(--il-cyan)]">{t.MicDropAccent}</span>
        {parts.slice(1).join(t.MicDropAccent)}
      </>
    );
  };

  const cards = [
    {
      sector: t.Card1_Sector,
      title: t.Card1_Title,
      pain: t.Card1_Pain,
      outcome: t.Card1_Outcome,
      status: t.Card1_Status,
      statusColor: "var(--il-cyan)",
      Story: Card1Story,
      testId: "pl-card-01",
    },
    {
      sector: t.Card2_Sector,
      title: t.Card2_Title,
      pain: t.Card2_Pain,
      outcome: t.Card2_Outcome,
      status: t.Card2_Status,
      statusColor: "white",
      Story: Card2Story,
      testId: "pl-card-02",
    },
    {
      sector: t.Card3_Sector,
      title: t.Card3_Title,
      pain: t.Card3_Pain,
      outcome: t.Card3_Outcome,
      status: t.Card3_Status,
      statusColor: "var(--il-cyan)",
      Story: Card3Story,
      testId: "pl-card-03",
    },
  ];

  return (
    <section
      id="performance-layer"
      data-testid="performance-layer"
      className="relative w-full min-h-screen py-32 px-6 lg:px-12 font-sans flex flex-col items-center overflow-hidden bg-transparent"
      style={{
        "--il-white": "#FFFFFF",
        "--il-cyan":  "#00E5FF",
        "--il-steel": "#1B3245",
        "--il-navy":  "#112332",
        "--il-amber": "#FFB300",
      }}
    >
      <style>{styles}</style>

      {/* Background Hexagon Matrix */}
      <div className="absolute inset-0 z-0 opacity-20 animate-pulse pointer-events-none" style={{ animationDuration: "6s" }}>
        <HexMatrix />
      </div>

      {/* Header */}
      <div className="relative z-10 max-w-[90rem] mx-auto text-center mb-24">
        <h2
          className="text-5xl md:text-7xl font-light tracking-tighter text-white"
          data-testid="pl-title"
        >
          {t.Title}{" "}
          <span className="text-[var(--il-cyan)] font-normal whitespace-nowrap">
            {t.Subtitle}
          </span>
        </h2>
        <p
          className="text-xl md:text-2xl text-white/50 max-w-4xl mx-auto mt-6 leading-relaxed font-light"
          data-testid="pl-body"
        >
          {t.Body}
        </p>
      </div>

      {/* Cards Grid */}
      <div className="relative z-10 grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12 w-full max-w-[90rem] mx-auto">
        {cards.map((c) => (
          <article
            key={c.testId}
            data-testid={c.testId}
            className="relative flex flex-col p-12 lg:p-14 rounded-3xl bg-white/[0.02] backdrop-blur-xl border border-white/10 text-left overflow-hidden shadow-[0_20px_50px_-10px_rgba(0,0,0,0.5)]"
          >
            <span className="text-xs font-mono tracking-[0.2em] text-white/40 uppercase mb-6 block">
              {c.sector}
            </span>
            <h3 className="text-3xl lg:text-4xl font-normal text-white mb-10 tracking-tight leading-snug">
              {c.title}
            </h3>

            <c.Story />

            {/* Status label below graphic */}
            <span
              className="block font-mono text-xs tracking-[0.22em] mt-6 mb-2"
              style={{
                color: c.statusColor,
                textShadow:
                  c.statusColor === "var(--il-cyan)"
                    ? "0 0 10px rgba(0,229,255,0.55)"
                    : "0 0 8px rgba(255,255,255,0.35)",
              }}
            >
              {c.status}
            </span>

            {/* Ledger */}
            <div className="flex flex-col gap-6 mt-auto pt-10 border-t border-white/10">
              <p className="text-base text-white/40 font-light leading-relaxed m-0">
                {c.pain}
              </p>
              <p className="text-lg text-white font-medium m-0">
                {c.outcome}
              </p>
            </div>
          </article>
        ))}
      </div>

      {/* Mic-Drop Banner */}
      <div
        data-testid="pl-mic-banner"
        className="relative z-10 mt-20 w-full max-w-5xl mx-auto py-8 px-12 rounded-2xl bg-white/[0.02] backdrop-blur-xl border border-white/10 flex justify-center items-center shadow-[0_15px_40px_-10px_rgba(0,0,0,0.6)]"
      >
        <p
          className="text-2xl lg:text-3xl font-light tracking-wide text-center text-white m-0 leading-snug"
          data-testid="pl-mic"
        >
          {renderMicDrop()}
        </p>
      </div>
    </section>
  );
};

const styles = `
/* ─── Card 1 · Voice bars — staggered infinite bounce ─── */
@keyframes plVBar {
  0%, 100% { height: 18%; }
  50%      { height: 100%; }
}
.pl-vBar {
  background: var(--il-cyan);
  height: 18%;
  box-shadow: 0 0 14px var(--il-cyan);
  animation: plVBar 1.1s ease-in-out infinite;
  transform-origin: 50% 100%;
  will-change: height;
}

/* ─── Card 2 · Sorting rows — left expand, right shrink ─── */
@keyframes plSyncL {
  0%,  10% { width: 25%; background: rgba(255,255,255,0.20); box-shadow: none; }
  50%, 70% { width: 100%; background: var(--il-cyan); box-shadow: 0 0 12px var(--il-cyan); }
  100%     { width: 25%; background: rgba(255,255,255,0.20); box-shadow: none; }
}
@keyframes plSyncR {
  0%,  10% { width: 75%; background: rgba(255,255,255,0.20); }
  50%, 70% { width: 0%;   background: rgba(255,255,255,0.50); }
  100%     { width: 75%; background: rgba(255,255,255,0.20); }
}
.pl-syncL { animation: plSyncL 2.8s ease-in-out infinite; will-change: width, background; }
.pl-syncR { animation: plSyncR 2.8s ease-in-out infinite; will-change: width, background; }

/* ─── Card 3 · Vault spin + accent line pulse ─── */
@keyframes plVSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
.pl-vSpin { animation: plVSpin 6s linear infinite; }

@keyframes plVAccent {
  0%, 100% { width: 0px;  background: var(--il-cyan); opacity: 0.2; }
  50%      { width: 24px; background: var(--il-cyan); opacity: 1;   }
}
.pl-vAccent { animation: plVAccent 1.6s ease-in-out infinite; }

@media (prefers-reduced-motion: reduce) {
  .pl-vBar, .pl-syncL, .pl-syncR, .pl-vSpin, .pl-vAccent {
    animation: none !important;
  }
}
`;

export default PerformanceLayer;
